import 'altcha';
import { register } from './auth-api';
import { API_BASE } from '../network/api';

const devUsername = import.meta.env.DEV ? 'admin' : '';
const devEmail = import.meta.env.DEV ? 'admin@tactus.dev' : '';
const devPassword = import.meta.env.DEV ? 'tactus123' : '';

const INPUT_CLS =
  'w-full px-3 py-2.5 bg-surface text-text border border-border rounded outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow';
const TOGGLE_CLS =
  'absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-text-dim hover:text-gold bg-transparent border-0 cursor-pointer';

export function renderRegister(root: HTMLElement, onSuccess: () => void): () => void {
  root.innerHTML = `
    <style>
      @keyframes reg-shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-5px); } 40%,80% { transform: translateX(5px); } }
      .reg-shake { animation: reg-shake 320ms ease; }
      @media (prefers-reduced-motion: reduce) { .reg-shake { animation: none; } }
    </style>
    <div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient">
      <form class="w-full max-w-[420px] bg-auth-card border border-border rounded-lg p-8 shadow-lift" id="register-form" novalidate>
        <h1 class="mb-2 font-heading text-2xl text-gold tracking-wide">Create an account</h1>
        <p class="mb-6 text-sm text-text-dim">It takes less than a minute.</p>
        <div id="register-error" class="mb-3.5 px-3 py-2.5 bg-error/[0.08] border border-error/40 rounded text-error text-[13px]" hidden></div>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">Username</span>
          <input type="text" name="username" minlength="3" maxlength="32" pattern="[a-zA-Z0-9_-]+" required value="${devUsername}"
            class="${INPUT_CLS}" />
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">Email</span>
          <input type="email" name="email" autocomplete="email" required value="${devEmail}"
            class="${INPUT_CLS}" />
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">Phone <span class="opacity-60">(optional)</span></span>
          <input type="tel" name="phone" autocomplete="tel" placeholder="+1 555 000 0000"
            class="${INPUT_CLS}" />
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">Password (min 8 chars)</span>
          <span class="relative block">
            <input type="password" name="password" autocomplete="new-password" minlength="8" required value="${devPassword}"
              class="${INPUT_CLS} pr-14" />
            <button type="button" class="${TOGGLE_CLS}" data-toggle="password" aria-label="Show password">Show</button>
          </span>
        </label>
        <label class="block mb-3.5">
          <span class="block mb-1.5 text-[13px] text-text-dim">Confirm password</span>
          <span class="relative block">
            <input type="password" name="confirm" autocomplete="new-password" minlength="8" required value="${devPassword}"
              class="${INPUT_CLS} pr-14" />
            <button type="button" class="${TOGGLE_CLS}" data-toggle="confirm" aria-label="Show password">Show</button>
          </span>
        </label>
        <label class="flex items-start gap-2.5 mb-3.5 cursor-pointer select-none" id="register-terms-row">
          <input type="checkbox" name="terms" class="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-gold)]" />
          <span class="text-[12px] leading-snug text-text-dim">
            I agree to the <a href="/legal/terms-of-service" target="_blank" rel="noopener">Terms of Service</a>
            and <a href="/legal/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>
          </span>
        </label>
        <div class="mb-3.5">
          <altcha-widget challenge="${API_BASE}/auth/altcha-challenge" auto="onload" name="altcha"></altcha-widget>
        </div>
        <button type="submit" class="inline-block w-full p-3 bg-gold text-bg font-bold border-0 rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed" id="register-btn">Create account</button>
        <p class="mt-5 text-[13px] text-text-dim text-center">
          Have an account? <a href="/login">Sign in</a>
        </p>
      </form>
    </div>
  `;

  const form = root.querySelector('#register-form') as HTMLFormElement;
  const errEl = root.querySelector('#register-error') as HTMLDivElement;
  const btn = root.querySelector('#register-btn') as HTMLButtonElement;
  const termsRow = root.querySelector('#register-terms-row') as HTMLLabelElement;

  // Show/hide toggles on both password fields.
  const onToggle = (e: Event) => {
    const t = e.target as HTMLElement;
    if (!t.dataset?.toggle) return;
    const input = form.querySelector(`input[name="${t.dataset.toggle}"]`) as HTMLInputElement | null;
    if (!input) return;
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    t.textContent = show ? 'Hide' : 'Show';
    t.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
  };
  form.addEventListener('click', onToggle);

  const fail = (msg: string) => {
    errEl.textContent = msg;
    errEl.hidden = false;
  };

  const handler = async (e: Event) => {
    e.preventDefault();
    errEl.hidden = true;
    const fd = new FormData(form);
    const username = String(fd.get('username') ?? '').trim();
    const password = String(fd.get('password') ?? '');
    const confirm = String(fd.get('confirm') ?? '');
    const phone = String(fd.get('phone') ?? '').trim();

    if (!username) return fail('Pick a username — the street needs a name for you.');
    if (password.length < 8) return fail('Password must be at least 8 characters.');
    if (password !== confirm) return fail("Passwords don't match.");
    if (phone && !/^[+\d][\d\s\-().]{4,28}$/.test(phone)) return fail('That phone number does not look right.');
    if (!fd.get('terms')) {
      termsRow.classList.remove('reg-shake');
      void termsRow.offsetWidth; // restart the animation
      termsRow.classList.add('reg-shake');
      return fail('You must accept the Terms of Service and Privacy Policy.');
    }
    const altchaPayload = fd.get('altcha') as string | null;
    if (!altchaPayload) return fail('Please wait for verification to complete.');

    btn.disabled = true;
    btn.textContent = 'Creating…';
    try {
      await register(
        String(fd.get('email') ?? ''),
        password,
        altchaPayload,
        username,
        phone || undefined,
        true,
      );
      onSuccess();
    } catch (err) {
      fail((err as Error).message);
      btn.disabled = false;
      btn.textContent = 'Create account';
    }
  };
  form.addEventListener('submit', handler);
  return () => {
    form.removeEventListener('submit', handler);
    form.removeEventListener('click', onToggle);
  };
}
